import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';

// v2 の Tailwind は prefix(tw) 設定のため、素の 'hidden'/'flex' では CSS が生成されない。
// coupon/category モーダルと同じく tw: prefix 付きクラスでオーバーレイの表示/非表示を切り替える。
const HIDDEN_CLASS = 'tw:hidden';
const FLEX_CLASS = 'tw:flex';

/**
 * 領収書モーダルコンポーネント
 *
 * @element kd-receipt-modal
 * @description 領収書の宛名・但し書き入力モーダルを制御し、iframe印刷を実行する。
 *              モーダルのHTML構造はLiquidで出力し、このコンポーネントは開閉・印刷のみ担当。
 */
@customElement('kd-receipt-modal')
export class KdReceiptModal extends LitElement {
  createRenderRoot() {
    return this;
  }

  private boundHandleReceiptButtonClick: ((e: Event) => void) | null = null;

  private boundHandleCloseClick: (() => void) | null = null;

  private boundHandlePrintClick: (() => void) | null = null;

  private boundHandleBackdropClick: ((e: Event) => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    this.boundHandleReceiptButtonClick = this.handleReceiptButtonClick.bind(this);
    this.boundHandleCloseClick = this.handleCloseClick.bind(this);
    this.boundHandlePrintClick = this.handlePrintClick.bind(this);
    this.boundHandleBackdropClick = this.handleBackdropClick.bind(this);

    // 領収書ボタン（各注文に配置）
    const receiptButtons = document.querySelectorAll(SELECTORS.ACCOUNT.RECEIPT_BUTTON);
    receiptButtons.forEach((btn) => {
      btn.addEventListener('click', this.boundHandleReceiptButtonClick!);
    });

    // モーダル内のボタン
    const closeBtn = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_MODAL_CLOSE);
    if (closeBtn) {
      closeBtn.addEventListener('click', this.boundHandleCloseClick!);
    }

    const printBtn = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_MODAL_PRINT);
    if (printBtn) {
      printBtn.addEventListener('click', this.boundHandlePrintClick!);
    }

    // バックドロップクリック
    const backdrop = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_MODAL_BACKDROP);
    if (backdrop) {
      backdrop.addEventListener('click', this.boundHandleBackdropClick!);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.boundHandleReceiptButtonClick) {
      const receiptButtons = document.querySelectorAll(SELECTORS.ACCOUNT.RECEIPT_BUTTON);
      receiptButtons.forEach((btn) => {
        btn.removeEventListener('click', this.boundHandleReceiptButtonClick!);
      });
    }

    const closeBtn = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_MODAL_CLOSE);
    if (closeBtn && this.boundHandleCloseClick) {
      closeBtn.removeEventListener('click', this.boundHandleCloseClick);
    }

    const printBtn = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_MODAL_PRINT);
    if (printBtn && this.boundHandlePrintClick) {
      printBtn.removeEventListener('click', this.boundHandlePrintClick);
    }

    const backdrop = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_MODAL_BACKDROP);
    if (backdrop && this.boundHandleBackdropClick) {
      backdrop.removeEventListener('click', this.boundHandleBackdropClick);
    }

    this.boundHandleReceiptButtonClick = null;
    this.boundHandleCloseClick = null;
    this.boundHandlePrintClick = null;
    this.boundHandleBackdropClick = null;
  }

  private getModal(): HTMLElement | null {
    return this.querySelector(SELECTORS.ACCOUNT.RECEIPT_MODAL_BACKDROP);
  }

  private handleReceiptButtonClick(e: Event): void {
    const target = e.currentTarget as HTMLElement;
    const orderId = target.dataset.orderId;
    if (!orderId) return;

    const targetIframeId = `receipt_iframe_${orderId}`;
    const iframe = document.getElementById(targetIframeId) as HTMLIFrameElement | null;
    if (!iframe) return;

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) return;

    // 前回の宛名を復元
    const nameInput = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_NAME_INPUT) as HTMLInputElement | null;
    const iframeName = iframeDoc.getElementById('receiptNameInput');
    if (nameInput && iframeName) {
      nameInput.value = iframeName.innerText;
    }

    // targetId をデータ属性で保持
    const modal = this.getModal();
    if (modal) {
      modal.dataset.targetId = targetIframeId;
      modal.classList.remove(HIDDEN_CLASS);
      modal.classList.add(FLEX_CLASS);
    }
  }

  private handleCloseClick(): void {
    const modal = this.getModal();
    if (modal) {
      modal.classList.add(HIDDEN_CLASS);
      modal.classList.remove(FLEX_CLASS);
    }
  }

  private handlePrintClick(): void {
    const modal = this.getModal();
    if (!modal) return;

    const targetId = modal.dataset.targetId;
    if (!targetId) return;

    modal.classList.add(HIDDEN_CLASS);
    modal.classList.remove(FLEX_CLASS);

    const nameInput = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_NAME_INPUT) as HTMLInputElement | null;
    const titleInput = this.querySelector(SELECTORS.ACCOUNT.RECEIPT_TITLE_INPUT) as HTMLInputElement | null;
    const iframe = document.getElementById(targetId) as HTMLIFrameElement | null;
    if (!iframe) return;

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) return;

    const iframeName = iframeDoc.getElementById('receiptNameInput');
    if (iframeName && nameInput) {
      iframeName.innerText = nameInput.value;
    }

    const iframeTitle = iframeDoc.getElementById('receiptTitleInput');
    if (iframeTitle && titleInput) {
      if (titleInput.value) {
        iframeTitle.textContent = `\u4F46\u3057${titleInput.value}\n\u4E0A\u8A18\u3001\u6B63\u306B\u9818\u53CE\u81F4\u3057\u307E\u3057\u305F\u3002`;
      } else {
        iframeTitle.textContent = '';
      }
    }

    iframe.contentWindow?.print();
  }

  private handleBackdropClick(e: Event): void {
    // バックドロップ自体がクリックされた場合のみ閉じる（子要素のクリックは無視）
    if (e.target === e.currentTarget) {
      const modal = this.getModal();
      if (modal) {
        modal.classList.add(HIDDEN_CLASS);
        modal.classList.remove(FLEX_CLASS);
      }
    }
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-receipt-modal': KdReceiptModal;
  }
}
